import React, { useMemo, useState } from "react";
import { Input, Checkbox, Tooltip } from "antd";

import "./style.scoped.scss";

/**
 * Checkbox picker over the project's documents. One instance per CER section, so
 * the document list is fetched once by the modal and passed in rather than
 * loaded here.
 *
 * Selection order is preserved: for the literature section the assignment order
 * *is* the article numbering, so a newly-ticked document is appended rather than
 * sorted into place.
 */
const SourceDocsPicker = ({ documents = [], value = [], onChange }) => {
    const [filter, setFilter] = useState("");

    const sorted = useMemo(
        () =>
            [...documents].sort((a, b) =>
                (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()),
            ),
        [documents],
    );

    const selectedSet = useMemo(() => new Set(value || []), [value]);

    const filtered = useMemo(() => {
        const tokens = filter
            .split(/\s+/g)
            .filter(Boolean)
            .map((t) => t.toLowerCase());
        if (tokens.length === 0) {
            return sorted;
        }
        return sorted.filter((d) => {
            const name = (d.name || "").toLowerCase();
            return tokens.every((t) => name.includes(t));
        });
    }, [sorted, filter]);

    const selectable = useMemo(() => filtered.filter((d) => d.hasPdf), [filtered]);

    const selectAllState = useMemo(() => {
        if (selectable.length === 0) {
            return { checked: false, indeterminate: false };
        }
        const count = selectable.filter((d) => selectedSet.has(d._id)).length;
        if (count === 0) {
            return { checked: false, indeterminate: false };
        }
        if (count === selectable.length) {
            return { checked: true, indeterminate: false };
        }
        return { checked: false, indeterminate: true };
    }, [selectable, selectedSet]);

    const emit = (next) => {
        onChange && onChange(next);
    };

    const toggle = (id) => {
        emit(selectedSet.has(id) ? value.filter((v) => v !== id) : [...value, id]);
    };

    const toggleAll = () => {
        if (selectAllState.checked) {
            const removing = new Set(selectable.map((d) => d._id));
            emit(value.filter((id) => !removing.has(id)));
            return;
        }
        const added = selectable.map((d) => d._id).filter((id) => !selectedSet.has(id));
        emit([...value, ...added]);
    };

    return (
        <div className="cer-docs-picker">
            {documents.length === 0 ? (
                <div className="empty">No source documents available in this project.</div>
            ) : (
                <>
                    <Input.Search
                        placeholder="Search documents"
                        onSearch={(v) => setFilter(v)}
                        allowClear
                    />
                    <div className="select-all" onClick={toggleAll}>
                        <Checkbox
                            checked={selectAllState.checked}
                            indeterminate={selectAllState.indeterminate}
                            onClick={(e) => e.stopPropagation()}
                            onChange={toggleAll}
                        />
                        <span className="label">
                            Select all ({filtered.length} document
                            {filtered.length !== 1 ? "s" : ""})
                        </span>
                    </div>
                    <div className="files">
                        {filtered.map((d) => (
                            <Tooltip
                                key={d._id}
                                title={d.hasPdf ? null : "Only PDF files are supported"}
                            >
                                <div
                                    className={`document${d.hasPdf ? "" : " disabled"}`}
                                    onClick={() => d.hasPdf && toggle(d._id)}
                                >
                                    <Checkbox
                                        checked={selectedSet.has(d._id)}
                                        disabled={!d.hasPdf}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => toggle(d._id)}
                                    />
                                    <span className="name">{d.name}</span>
                                </div>
                            </Tooltip>
                        ))}
                    </div>
                    <div className="footer">
                        {selectedSet.size} document{selectedSet.size !== 1 ? "s" : ""} selected
                    </div>
                </>
            )}
        </div>
    );
};

export default SourceDocsPicker;
